/**
 * Source unique des données SEO par route.
 *
 * Ce module est volontairement écrit en CommonJS (`module.exports`) afin d'être
 * utilisé À LA FOIS par :
 *   - l'application React (src/components/RouteSeo.jsx, Seo.jsx) côté navigateur ;
 *   - le générateur de pré-rendu statique (scripts/prerender-seo.cjs) côté Node.
 *
 * Garder une seule définition évite toute désynchronisation entre le SEO rendu
 * en JavaScript (vu par Googlebot) et le SEO injecté dans le HTML statique
 * (vu par les robots sociaux qui n'exécutent pas le JavaScript).
 */

const SITE_URL = "https://www.nouzha.ma";
const SITE_NAME = "Nouzha";
const DEFAULT_IMAGE =
  "https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg";
const DEFAULT_DESCRIPTION =
  "Nouzha, agence de voyage de luxe au Maroc : séjours haut de gamme, circuits sur mesure, transport privé et tours guidés à Marrakech, Fès, Casablanca, le Sahara et Chefchaouen. Réservez votre voyage d'exception au Maroc.";

// Fil d'Ariane Schema.org
const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

// Service Schema.org (réutilisé pour les pages d'offres)
const service = (name, description) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  name,
  description,
  provider: { "@type": "TravelAgency", name: "Nouzha", url: `${SITE_URL}/` },
  areaServed: { "@type": "Country", name: "Maroc" },
  availableLanguage: ["fr", "en", "ar"],
});

// FAQ minimale (booste les rich results "Voyage Maroc")
const faq = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Quelles destinations propose Nouzha au Maroc ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Nouzha organise des voyages dans tout le Maroc : Marrakech, Fès, Casablanca, Rabat, Tanger, Agadir, Chefchaouen, Essaouira, Ouarzazate et le désert du Sahara (Merzouga).",
      },
    },
    {
      "@type": "Question",
      name: "Puis-je créer un circuit sur mesure au Maroc ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui. Nouzha conçoit des circuits sur mesure au Maroc adaptés à vos envies, votre budget et vos dates, avec transport privé, guides et hébergements haut de gamme.",
      },
    },
    {
      "@type": "Question",
      name: "Nouzha propose-t-elle le transport privé au Maroc ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui, Nouzha propose un service de transport privé avec chauffeur dans tout le Maroc, ainsi que la location de véhicules pour vos déplacements.",
      },
    },
  ],
};

// Table de correspondance route -> SEO
const ROUTES = {
  "/": {
    title: null, // titre par défaut de la marque
    description:
      "Nouzha, agence de voyage de luxe au Maroc : séjours haut de gamme, circuits sur mesure, transport privé et tours guidés à Marrakech, Fès, Casablanca, le Sahara et Chefchaouen. Réservez votre voyage d'exception au Maroc.",
    keywords:
      "voyage Maroc, tourisme Maroc, agence de voyage Maroc, circuit Maroc, séjour Maroc, voyage de luxe Maroc, voyage Marrakech, voyage Fès, désert Sahara Maroc",
    jsonLd: [faq],
  },
  "/services": {
    title: "Nos services de voyage au Maroc",
    description:
      "Découvrez tous les services Nouzha au Maroc : séjours, circuits sur mesure, transport privé, location de véhicules et tours guidés. Une expérience de voyage haut de gamme, clé en main.",
    keywords:
      "services voyage Maroc, agence voyage Maroc, séjour Maroc, circuit Maroc, transport privé Maroc, tour guidé Maroc",
    jsonLd: [
      service(
        "Services de voyage au Maroc",
        "Séjours, circuits sur mesure, transport privé, location et tours guidés au Maroc."
      ),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Services", path: "/services" },
      ]),
    ],
  },
  "/sejour": {
    title: "Séjours de luxe au Maroc — Hôtels & Riads d'exception",
    description:
      "Réservez votre séjour de luxe au Maroc avec Nouzha : hôtels 5 étoiles et riads d'exception à Marrakech, Fès, Casablanca et plus. Confort premium et service sur mesure.",
    keywords:
      "séjour Maroc, séjour luxe Maroc, hôtel luxe Maroc, riad Marrakech, riad de luxe Maroc, réservation hôtel Maroc, vacances Maroc",
    jsonLd: [
      service(
        "Séjours de luxe au Maroc",
        "Hôtels 5 étoiles et riads d'exception à Marrakech, Fès, Casablanca et dans tout le Maroc."
      ),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Séjours", path: "/sejour" },
      ]),
    ],
  },
  "/circuit": {
    title: "Circuits au Maroc — Voyages organisés & itinéraires d'exception",
    description:
      "Explorez le Maroc avec les circuits Nouzha : routes impériales, désert du Sahara, vallées de l'Atlas et villes bleues. Circuits organisés haut de gamme avec guide et transport privé.",
    keywords:
      "circuit Maroc, circuit organisé Maroc, voyage organisé Maroc, circuit désert Maroc, circuit Sahara, circuit Marrakech, route impériale Maroc",
    jsonLd: [
      service(
        "Circuits touristiques au Maroc",
        "Circuits organisés haut de gamme au Maroc : désert du Sahara, Atlas, villes impériales, avec guide et transport privé."
      ),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Circuits", path: "/circuit" },
      ]),
    ],
  },
  "/circuit-sur-mesure": {
    title: "Circuit sur mesure au Maroc — Voyage personnalisé",
    description:
      "Créez votre circuit sur mesure au Maroc avec Nouzha. Itinéraire personnalisé selon vos envies, votre budget et vos dates : transport privé, guides experts et hébergements d'exception.",
    keywords:
      "circuit sur mesure Maroc, voyage personnalisé Maroc, voyage sur mesure Maroc, itinéraire Maroc, circuit privé Maroc",
    jsonLd: [
      service(
        "Circuit sur mesure au Maroc",
        "Voyage personnalisé au Maroc conçu selon vos envies, avec transport privé, guides et hébergements haut de gamme."
      ),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Circuit sur mesure", path: "/circuit-sur-mesure" },
      ]),
    ],
  },
  "/transport": {
    title: "Transport privé au Maroc — Chauffeur & transferts",
    description:
      "Service de transport privé avec chauffeur au Maroc : transferts aéroport, déplacements inter-villes et excursions. Véhicules haut de gamme et chauffeurs professionnels avec Nouzha.",
    keywords:
      "transport privé Maroc, chauffeur privé Maroc, transfert aéroport Maroc, transport touristique Maroc, voiture avec chauffeur Maroc",
    jsonLd: [
      service(
        "Transport privé au Maroc",
        "Transferts aéroport, déplacements inter-villes et excursions avec chauffeur privé au Maroc."
      ),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Transport", path: "/transport" },
      ]),
    ],
  },
  "/location": {
    title: "Location de véhicules au Maroc",
    description:
      "Louez un véhicule au Maroc avec Nouzha : voitures, 4x4 et minibus pour explorer le pays en toute liberté. Tarifs transparents et service fiable.",
    keywords:
      "location voiture Maroc, location 4x4 Maroc, louer voiture Maroc, location véhicule Maroc, car rental Morocco",
    jsonLd: [
      service(
        "Location de véhicules au Maroc",
        "Voitures, 4x4 et minibus en location pour explorer le Maroc en toute liberté."
      ),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Location", path: "/location" },
      ]),
    ],
  },
  "/guided-tours": {
    title: "Tours guidés au Maroc — Excursions avec guide",
    description:
      "Visites et excursions guidées au Maroc avec Nouzha : médinas, sites historiques, désert et nature. Guides locaux experts pour une découverte authentique du Maroc.",
    keywords:
      "tour guidé Maroc, excursion Maroc, visite guidée Maroc, guide touristique Maroc, excursion Marrakech, excursion désert Maroc",
    jsonLd: [
      service(
        "Tours guidés au Maroc",
        "Visites et excursions guidées au Maroc : médinas, sites historiques, désert et nature avec guides locaux."
      ),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Tours guidés", path: "/guided-tours" },
      ]),
    ],
  },
  "/contact": {
    title: "Contact — Agence de voyage au Maroc",
    description:
      "Contactez Nouzha pour organiser votre voyage au Maroc : séjours, circuits sur mesure et transport privé. Notre équipe vous répond et vous accompagne. Tél : +212 651-913292.",
    keywords:
      "contact Nouzha, agence voyage Maroc contact, réserver voyage Maroc, devis voyage Maroc",
    jsonLd: [
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
    ],
  },
  "/about": {
    title: "À propos — Votre agence de voyage de luxe au Maroc",
    description:
      "Découvrez Nouzha, agence de voyage de luxe au Maroc. Notre passion : créer des voyages d'exception, authentiques et sur mesure à travers tout le Maroc.",
    keywords: "à propos Nouzha, agence voyage Maroc, voyage de luxe Maroc",
    jsonLd: [
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "À propos", path: "/about" },
      ]),
    ],
  },
  "/agency-partnership": {
    title: "Partenariat agences de voyage au Maroc",
    description:
      "Devenez partenaire de Nouzha. Agences de voyage et professionnels du tourisme : collaborez avec nous pour proposer des voyages d'exception au Maroc.",
    keywords:
      "partenariat agence voyage Maroc, B2B tourisme Maroc, partenaire voyage Maroc",
  },
  "/pro": {
    title: "Espace professionnels du tourisme au Maroc",
    description:
      "Espace dédié aux professionnels du tourisme au Maroc. Gérez vos offres et collaborez avec Nouzha.",
    keywords: "espace pro tourisme Maroc, professionnels voyage Maroc",
  },
  "/help": {
    title: "Aide & FAQ — Réservations et voyages au Maroc",
    description:
      "Centre d'aide Nouzha : réponses à vos questions sur les réservations, paiements, séjours et circuits au Maroc.",
    keywords: "aide Nouzha, FAQ voyage Maroc, questions réservation Maroc",
  },
  "/safety": {
    title: "Sécurité & confiance",
    description:
      "Voyagez en toute sérénité avec Nouzha : nos engagements de sécurité et de confiance pour votre voyage au Maroc.",
  },
  "/cancellation": {
    title: "Conditions d'annulation",
    description:
      "Consultez la politique d'annulation de Nouzha pour vos réservations de voyage au Maroc.",
  },
  "/paiement-securise": {
    title: "Paiement sécurisé",
    description:
      "Réglez votre voyage au Maroc en toute sécurité avec Nouzha. Paiement sécurisé et protection de vos données.",
  },
  "/cgv": {
    title: "Conditions générales de vente",
    description:
      "Conditions générales de vente de Nouzha, agence de voyage au Maroc.",
  },
  "/legal/mentions": {
    title: "Mentions légales",
    description: "Mentions légales de Nouzha, agence de voyage au Maroc.",
  },
  "/legal/terms": {
    title: "Conditions d'utilisation",
    description: "Conditions d'utilisation du site Nouzha.",
  },
  "/legal/privacy": {
    title: "Politique de confidentialité",
    description:
      "Politique de confidentialité de Nouzha : protection et traitement de vos données personnelles.",
  },
  "/legal/cookies": {
    title: "Politique cookies",
    description: "Politique de gestion des cookies du site Nouzha.",
  },
};

// Routes privées / techniques : à désindexer
const NOINDEX_PREFIXES = [
  "/login",
  "/register",
  "/profile",
  "/admin",
  "/adminpanel",
  "/debug-auth",
  "/invoice-request",
  "/payment",
  "/reservation",
];

module.exports = {
  SITE_URL,
  SITE_NAME,
  DEFAULT_IMAGE,
  DEFAULT_DESCRIPTION,
  breadcrumb,
  service,
  faq,
  ROUTES,
  NOINDEX_PREFIXES,
};
